import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faInfoCircle, faUserGraduate } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Notify, ErrorRetry, HeadingItem, TrainingPopup } from 'components';
import { AssignmentCard } from 'components/load-assignments';
import { TripContext } from 'contexts';
import { LoadIndicator } from 'devextreme-react/load-indicator';
import React, { Fragment, useContext, useEffect, useRef, useState } from 'react';
import { useLoadManagerService, useNavigation, useSettingService, useTripService } from 'services';
import { TripStatus } from 'services/enums';
import { ReconcileReason, Trip, User } from 'services/types';
import { authentication } from 'utils';
import './load-assignments.scss';

export const LoadAssignmentsPage = () => {

    const [isError, setIsError] = useState<boolean>(false)
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [isTrainingPopupVisible, setIsTrainingPopupVisible] = useState<boolean>(false);
    const [isVisibleErrorNotify, setIsVisibleErrorNotify] = useState<boolean>(false)

    const isFetchingData = useRef<boolean>(false)
    const tripContext = useContext(TripContext)

    const { getTrainingMode, getTrainerId } = useSettingService()
    const { initialNavigationSetup } = useNavigation()

    const isTrainingMode: boolean = getTrainingMode()
    const trainerId: string | undefined = getTrainerId()

    let { clearTrips } = useTripService()
    let { getLoadAssignments, updateReasons, loadDemoLoad } = useLoadManagerService()
    let { isTripSynced } = useTripService()
    let user: User | null = authentication.getUser()
    if (user == null) {
        authentication.loginWithRedirect()
    }
    let trips: Trip[] = tripContext.trips.toArray() ?? []
    let reasons: ReconcileReason[] = tripContext.reconcileReasons.toArray() ?? []
    const haveRequiredData = useRef<boolean>(false)
    const updateRequiredDataStatus = () => {
        const newTrips = tripContext.trips.toArray() ?? []
        const newReasons = tripContext.reconcileReasons.toArray() ?? []
        haveRequiredData.current = newTrips.length > 0 && newReasons.length > 0
    }
    updateRequiredDataStatus()

    useEffect(() => {
        const initialize = async () => {
            initialNavigationSetup()
            if (trips.length === 0 || reasons.length === 0) {
                setIsLoading(true)
            }
        }
        initialize()
    }, [])

    useEffect(() => {
        if (trainerId == null && isTrainingMode) {
            clearTrips()
            loadDemoLoad()
        }

        if (trainerId && isTrainingMode && trips.length === 0) {
            setIsLoading(true)
        }

    }, [trainerId, isTrainingMode])

    useEffect(() => {
        const getLoads = async () => {
            try {
                const driverId: string | null = isTrainingMode ? (trainerId ?? null) : (user?.driverId ?? null)
                
                if (driverId != null && driverId != '') {
                    await Promise.all([
                        getLoadAssignments(driverId!),
                        updateReasons()
                    ])
                }
            }
            catch (e) {
                if (haveRequiredData.current) {
                    setIsVisibleErrorNotify(true)
                } else {
                    setIsError(true)
                }
            }
            finally {
                isFetchingData.current = false
                setIsLoading(false)
                updateRequiredDataStatus()
            }
        }

        if (isLoading && !isFetchingData.current) {
            isFetchingData.current = true
            setIsError(false)
            setIsVisibleErrorNotify(false)
            getLoads()
        }

    }, [isLoading, user, trainerId])

    const onClickRetrieve = async (): Promise<void> => {
        setIsTrainingPopupVisible(false);
        // reload the data based on the trainer id
        setIsLoading(true);
    }

    const checkForUpdate = () => {
        setIsLoading(true)
    }
    const compareDate = (a: Date, b: Date): number => {
        if (a < b) return -1
        else if (a > b) return 1
        else return 0
    }

    const toStatusRank = (status: TripStatus, tripId: number): number => {
        let rank = 10
        if(status == TripStatus.Complete && isTripSynced(tripId) == false) {
            rank = 0
        } 
        switch (status) {
            case TripStatus.Started: rank = 1; break;
            case TripStatus.Ready: rank = 2; break;
            case TripStatus.Waiting: rank = 3; break;
            case TripStatus.Importing: rank = 4; break;
        }
        return rank
    }

    trips.sort((a: Trip, b: Trip): number => {
        let aRank = toStatusRank(a.status, a.id)
        let bRank = toStatusRank(b.status, b.id)

        if (aRank < bRank) return -1
        else if (bRank < aRank) return 1
        else return compareDate(a.dueOut, b.dueOut)
    })

    const errorRetryMessage = (): JSX.Element => {
        let message = isTrainingMode ?
            'No loads found. Change trainer or click retry to try getting your loads again.' :
            'No loads found. Enter training mode or click retry to try getting your loads again.'

        return <ErrorRetry
            headingText={'Notice'}
            message={message}
            onRetry={() => setIsLoading(true)}
            displayButtonOne={true}
            buttonOneText={isTrainingMode ? 'Change Trainer' : 'Training Mode'}
            buttonOneColor={isTrainingMode ? '#F2994A' : '#82BC24'}
            buttonOneIcon={faUserGraduate}
            onButtonOneClicked={() => setIsTrainingPopupVisible(true)}
            buttonOneWidth={isTrainingMode ? '123px' : '120px'}
        />
    }

    return (
        <Fragment>
            <aside className={'load-assignments-notice'} aria-label={'Load assignments notice'}>
                <FontAwesomeIcon className={'load-assignments-notice-icon'} icon={faInfoCircle as IconProp} aria-hidden={'true'} />
                <span>Assigned loads will no longer appear in <strong>Waiting</strong> status. Loads will be displayed in Load Manager once they are marked <strong>Ready</strong> by the warehouse.</span>
            </aside>
            <Fragment>
                <Notify isVisible={isVisibleErrorNotify} onRightSideIconClick={() => setIsLoading(true)} message={'Could not get your loads'} onLeftSideIconClick={() => { setIsVisibleErrorNotify(false) }}></Notify>
            </Fragment>
            <Fragment>
                {!isTrainingPopupVisible &&
                    <Fragment>
                        {(isLoading) ?
                            <div className={'card-material'} style={{ minHeight: '400px' }}>
                                <HeadingItem text={'Getting Your Loads'} />
                                <div className={'load-indicator'}>
                                    <LoadIndicator width={80} height={80} />
                                </div>
                            </div>
                            :
                            <div>
                                {(isError && !haveRequiredData.current) ?
                                    <ErrorRetry message={'Could not get loads.'} onRetry={() => setIsLoading(true)} />
                                    :
                                    <Fragment>
                                        {haveRequiredData.current ?
                                            <Fragment>
                                                {
                                                    <Fragment>
                                                        <div className={'tap-to-refresh'} onClick={checkForUpdate}>Tap to Refresh</div>
                                                        {
                                                            trips.map(load => <AssignmentCard key={load.loadNumber} loadId={load.id} />)
                                                        }
                                                    </Fragment>
                                                }
                                            </Fragment>
                                            :
                                            errorRetryMessage()
                                        }
                                    </Fragment>
                                }
                            </div>
                        }
                    </Fragment>
                }
            </Fragment>

            <TrainingPopup
                isVisible={isTrainingPopupVisible}
                onClickRetrieve={() => onClickRetrieve()}
                onClickClose={() => setIsTrainingPopupVisible(false)}
            />
        </Fragment>
    )
}
